import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube } from 'lucide-react';
import { settingsAPI } from '../../lib/api';
import { useSettings } from '../../contexts/SettingsContext';

export const Footer = () => {
  const { settings } = useSettings();
  
  // Default support links if none configured
  const supportLinks = settings.footer_support_links || [
    { label: "FAQ", url: "/faq" },
    { label: "Cancellation Policy", url: "/cancellation-policy" },
    { label: "Privacy Policy", url: "/privacy-policy" },
    { label: "Terms of Service", url: "/terms-of-service" }
  ];

  // Default explore links if none configured
  const exploreLinks = settings.footer_explore_links || [
    { label: "Adventure Tours", url: "/tours?category=adventure" },
    { label: "Marine Tours", url: "/tours?category=marine" },
    { label: "Food Tours", url: "/tours?category=food" },
    { label: "Night Tours", url: "/tours?category=night" },
    { label: "Tour Deals", url: "/tours?deals=true" }
  ];
  
  const brandDescription = settings.footer_brand_description || 
    "Discover the magic of Puerto Rico with our curated tours. From rainforest adventures to bioluminescent bays, we bring you the best of the island.";
  
  const copyright = settings.footer_copyright || "Tour Finder Puerto Rico. All rights reserved.";

  // Section visibility flags (default to true if not set)
  const showExplore = settings.show_footer_explore !== false;
  const showSupport = settings.show_footer_support !== false;
  const showContact = settings.show_footer_contact !== false;
  const showSocial = settings.show_footer_social !== false;

  // Calculate grid columns based on visible sections
  const visibleSections = [true, showExplore, showSupport, showContact].filter(Boolean).length;
  const gridColsClass = visibleSections === 4 
    ? 'lg:grid-cols-4' 
    : visibleSections === 3 
    ? 'lg:grid-cols-3' 
    : visibleSections === 2 
    ? 'lg:grid-cols-2' 
    : 'lg:grid-cols-1';

  return (
    <footer className="bg-foreground text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className={`grid grid-cols-1 md:grid-cols-2 ${gridColsClass} gap-12`}>
          {/* Brand - Always visible */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img 
                src={settings.site_logo_url} 
                alt="Tour Finder Icon" 
                className="h-10 w-10 sm:h-12 sm:w-12 object-contain flex-shrink-0"
              />
              <span className="font-heading text-lg sm:text-xl font-black tracking-tight">TOUR FINDER</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {brandDescription}
            </p>
            {showSocial && (
              <div className="flex gap-4">
                {settings.facebook_url && (
                  <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-secondary transition-colors" aria-label="Facebook">
                    <Facebook className="h-5 w-5" />
                  </a>
                )}
                {settings.instagram_url && (
                  <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-secondary transition-colors" aria-label="Instagram">
                    <Instagram className="h-5 w-5" />
                  </a>
                )}
                {settings.twitter_url && (
                  <a href={settings.twitter_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-secondary transition-colors" aria-label="Twitter">
                    <Twitter className="h-5 w-5" />
                  </a>
                )}
                {settings.youtube_url && (
                  <a href={settings.youtube_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-secondary transition-colors" aria-label="YouTube">
                    <Youtube className="h-5 w-5" />
                  </a>
                )}
                {!settings.facebook_url && !settings.instagram_url && !settings.twitter_url && !settings.youtube_url && (
                  <>
                    <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-secondary transition-colors" aria-label="Facebook">
                      <Facebook className="h-5 w-5" />
                    </a>
                    <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-secondary transition-colors" aria-label="Instagram">
                      <Instagram className="h-5 w-5" />
                    </a>
                    <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-secondary transition-colors" aria-label="Twitter">
                      <Twitter className="h-5 w-5" />
                    </a>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Quick Links / Explore - Conditionally visible */}
          {showExplore && (
            <div>
              <h4 className="font-heading text-lg font-semibold mb-4">Explore</h4>
              <ul className="space-y-3">
                {exploreLinks.map((link, index) => (
                  <li key={index}>
                    {link.url.startsWith('http') ? (
                      <a 
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-secondary transition-colors text-sm"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link 
                        to={link.url} 
                        className="text-gray-400 hover:text-secondary transition-colors text-sm"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Support - Conditionally visible */}
          {showSupport && (
            <div>
              <h4 className="font-heading text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-3">
                {supportLinks.map((link, index) => (
                  <li key={index}>
                    {link.url.startsWith('http') ? (
                      <a 
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-secondary transition-colors text-sm"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link 
                        to={link.url} 
                        className="text-gray-400 hover:text-secondary transition-colors text-sm"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
                {/* Service Providers Link */}
                <li>
                  <Link 
                    to="/service-providers" 
                    className="text-secondary hover:text-white transition-colors text-sm font-semibold flex items-center gap-1"
                    data-testid="service-providers-footer-link"
                  >
                    ★ Service Providers
                  </Link>
                </li>
                {/* Referral Agents Link */}
                <li>
                  <Link 
                    to="/referral-agents" 
                    className="text-secondary hover:text-white transition-colors text-sm font-semibold flex items-center gap-1"
                    data-testid="referral-agent-footer-link"
                  >
                    ★ Referral Agents
                  </Link>
                </li>
              </ul>
            </div>
          )}

          {/* Contact - Conditionally visible */}
          {showContact && (
            <div>
              <h4 className="font-heading text-lg font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-secondary mt-0.5" />
                  <span className="text-gray-400 text-sm">
                    {settings.address.split(',').map((line, i) => (
                      <span key={i}>{line.trim()}{i < settings.address.split(',').length - 1 && <br />}</span>
                    ))}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-secondary" />
                  <a href={`tel:${settings.phone_number.replace(/[\s()-]/g, '')}`} className="text-gray-400 hover:text-secondary transition-colors text-sm">
                    {settings.phone_number}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-secondary" />
                  <a href={`mailto:${settings.email}`} className="text-gray-400 hover:text-secondary transition-colors text-sm">
                    {settings.email}
                  </a>
                </li>
              </ul>
            </div>
          )}
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} {copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};
